import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { Check, X, Eye, Upload, FileText, Download, Layers } from 'lucide-react';
import { formatDateTime } from '../lib/format';
import { useToast } from '../hooks/use-toast';
import { EquipmentCell, EquipmentOptionLabel } from '../components/EquipmentCell';
import { useNavigate } from 'react-router-dom';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const Inspections = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ findings: '', inspection_date: '', pdf_report_filename: '', pdf_report_data: '' });
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueReason, setQueueReason] = useState('unavailable');
  const [queueNotes, setQueueNotes] = useState('');
  // Effective floor for the Inspection Date picker. Recomputed on dialog open
  // as max(parent.approved_at, today_manila) so a user can never backdate
  // relative to today AND never to before the parent request was approved.
  const [minInspectionDate, setMinInspectionDate] = useState('');
  const [approvedAtDate, setApprovedAtDate] = useState('');

  // today in Asia/Manila as YYYY-MM-DD (matches the server-side rule exactly,
  // client browser TZ irrelevant).
  const todayManila = () => {
    const ms = Date.now() + 8 * 60 * 60 * 1000;
    return new Date(ms).toISOString().slice(0, 10);
  };

  const canApprove = hasRole(user, 'super_admin','admin','approver');

  const load = useCallback(async () => {
    const r = await api.get('/inspections');
    setItems(r.data);
    const v = await api.get('/vehicles');
    setVehicles(v.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const vehById = (id) => vehicles.find((v) => v.id === id) || {};

  const openDetail = async (insp) => {
    const r = await api.get(`/inspections/${insp.id}`);
    setDetail(r.data);
    setEditMode(false);
    setEditForm({ findings: r.data.findings || '', inspection_date: r.data.inspection_date || '', pdf_report_filename: r.data.pdf_report_filename || '', pdf_report_data: r.data.pdf_report_data || '' });
    // Effective floor for the date picker = max(parent.approved_at, today).
    const t = todayManila();
    let approved = '';
    if (r.data.request_id) {
      try {
        const rq = await api.get(`/equipment-requests/${r.data.request_id}`);
        if (rq.data?.approved_at) approved = String(rq.data.approved_at).slice(0, 10);
      } catch (e) { /* non-blocking; backend still enforces */ }
    }
    setApprovedAtDate(approved);
    setMinInspectionDate(approved && approved > t ? approved : t);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast({ title: 'File too large', description: 'Max 10MB', variant: 'destructive' }); return; }
    const reader = new FileReader();
    reader.onload = () => setEditForm((p) => ({ ...p, pdf_report_filename: f.name, pdf_report_data: String(reader.result).split(',')[1] || '' }));
    reader.readAsDataURL(f);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/inspections/${detail.id}`, editForm);
      toast({ title: 'Inspection updated' });
      setEditMode(false);
      load();
      const r = await api.get(`/inspections/${detail.id}`); setDetail(r.data);
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const approve = async () => {
    try {
      const r = await api.post(`/inspections/${detail.id}/approve`, {});
      toast({ title: 'Inspection approved', description: 'Opening Mobilization Form…' });
      const mobId = r.data?.mobilization_id;
      setDetail(null); load();
      if (mobId) setTimeout(() => navigate(`/mobilizations/${mobId}/form`), 400);
    } catch (e) { toast({ title: 'Cannot approve', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const reject = async () => {
    if (!rejectReason.trim()) { toast({ title: 'Reason required', variant: 'destructive' }); return; }
    try {
      await api.post(`/inspections/${detail.id}/reject`, { rejection_reason: rejectReason });
      toast({ title: 'Rejected' });
      setRejectOpen(false); setDetail(null); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const downloadPdf = () => {
    if (!detail?.pdf_report_data) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${detail.pdf_report_data}`;
    link.download = detail.pdf_report_filename || 'inspection.pdf';
    link.click();
  };

  const submitQueue = async () => {
    try {
      const r = await api.post('/equipment-queues', { inspection_id: detail.id, reason: queueReason, notes: queueNotes });
      toast({ title: 'Sent to Equipment Queue', description: `Ref ${r.data.reference_number} — Queuing team will find an alternative.` });
      setQueueOpen(false); setDetail(null); setQueueNotes(''); setQueueReason('unavailable');
      load();
      setTimeout(() => navigate(`/equipment-queues?focus=${r.data.id}`), 600);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Inspections" description="Equipment inspections auto-generated from approved requests. Upload PDF report before approval." />
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-left text-slate-600">
              <tr><th className="p-3">Ref #</th><th className="p-3">Equipment</th><th className="p-3">Inspector</th><th className="p-3">PDF Report</th><th className="p-3">Created</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y">
              {items.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-500">No inspections yet.</td></tr> :
                items.map((it) => {
                  const v = vehById(it.vehicle_id);
                  return (
                    <tr key={it.id} className="table-row-hover">
                      <td className="p-3 font-mono text-xs">{it.reference_number}</td>
                      <td className="p-3"><div className="font-mono font-semibold">{v.equipment_code}</div><div className="text-xs text-slate-500">{v.description}</div></td>
                      <td className="p-3 text-xs">{it.inspector_name}</td>
                      <td className="p-3 text-xs">{it.pdf_report_filename ? <span className="text-emerald-700 inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Uploaded</span> : <span className="text-amber-700">Pending upload</span>}</td>
                      <td className="p-3 text-xs">{formatDateTime(it.created_at)}</td>
                      <td className="p-3"><StatusBadge status={it.status} type="request" /></td>
                      <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(it)}><Eye className="h-4 w-4" /></Button></td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inspection <span className="font-mono text-sm">{detail?.reference_number}</span></DialogTitle>
            <DialogDescription>Review findings and upload PDF report before approval.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Status"><StatusBadge status={detail.status} type="request" /></Info>
                <Info label="Inspector">{detail.inspector_name}</Info>
                <Info label="Equipment"><EquipmentCell vehicle={vehById(detail.vehicle_id)} /></Info>
                <Info label="Created">{formatDateTime(detail.created_at)}</Info>
              </div>
              {!editMode ? (
                <>
                  <Info label="Findings">{detail.findings || <span className="text-slate-400">No findings recorded.</span>}</Info>
                  {detail.pdf_report_filename ? (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded"><div className="flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-[hsl(217,91%,24%)]" /> {detail.pdf_report_filename}</div><Button variant="outline" size="sm" onClick={downloadPdf}><Download className="h-4 w-4 mr-1.5" /> Download</Button></div>
                  ) : <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded">PDF report upload required before approval.</div>}
                </>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label>Inspection Date</Label>
                    <Input
                      type="date"
                      min={minInspectionDate || undefined}
                      value={editForm.inspection_date}
                      onChange={(e) => setEditForm({ ...editForm, inspection_date: e.target.value })}
                      data-testid="inspection-date-input"
                    />
                    {minInspectionDate && (
                      <div className="mt-1 text-[11px] text-slate-500" data-testid="inspection-date-hint">
                        Must be on or after <b>{minInspectionDate}</b>
                        {approvedAtDate && approvedAtDate > todayManila()
                          ? ' (request approval date)'
                          : ' (today, Asia/Manila)'} — backdating is not allowed.
                      </div>
                    )}
                  </div>
                  <div><Label>Findings</Label><Textarea rows={4} value={editForm.findings} onChange={(e) => setEditForm({ ...editForm, findings: e.target.value })} /></div>
                  <div>
                    <Label>PDF Report (max 10MB)</Label>
                    <Input type="file" accept="application/pdf" onChange={onFile} />
                    {editForm.pdf_report_filename && <div className="text-xs text-emerald-700 mt-1">Selected: {editForm.pdf_report_filename}</div>}
                  </div>
                </div>
              )}
              {detail.rejection_reason && <div className="p-3 bg-red-50 border border-red-200 rounded"><div className="text-xs font-semibold text-red-800">Rejected</div><div className="text-sm text-red-900 mt-1">{detail.rejection_reason}</div></div>}
            </div>
          )}
          <DialogFooter className="gap-2">
            {!editMode ? (
              <>
                {detail?.status === 'pending' && <Button variant="outline" onClick={() => setEditMode(true)}><Upload className="h-4 w-4 mr-1.5" />Update / Upload</Button>}
                {detail?.status === 'pending' && <Button variant="outline" onClick={() => { setQueueOpen(true); setQueueReason('unavailable'); setQueueNotes(''); }} className="border-amber-400 text-amber-700 hover:bg-amber-50"><Layers className="h-4 w-4 mr-1.5" />Send to Queue</Button>}
                {detail?.status === 'pending' && canApprove && <Button variant="destructive" onClick={() => setRejectOpen(true)}><X className="h-4 w-4 mr-1.5" />Reject</Button>}
                {detail?.status === 'pending' && canApprove && <Button onClick={approve} className="bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4 mr-1.5" />Approve</Button>}
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
              </>
            ) : (
              <><Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button><Button onClick={saveEdit} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">Save</Button></>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Inspection</DialogTitle><DialogDescription>Rejection reason is mandatory.</DialogDescription></DialogHeader>
          <Label>Reason *</Label>
          <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          <DialogFooter><Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button><Button variant="destructive" onClick={reject}>Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={queueOpen} onOpenChange={setQueueOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-amber-700" /> Send to Equipment Queue</DialogTitle>
            <DialogDescription>The Queuing team will look for an operational alternative in the same equipment category. If none is available, this inspection waits in the category queue until one becomes operational.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason *</Label>
              <Select value={queueReason} onValueChange={setQueueReason}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unavailable">Equipment unavailable</SelectItem>
                  <SelectItem value="damaged">Equipment damaged</SelectItem>
                  <SelectItem value="not_arrived">Equipment not yet arrived</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={3} value={queueNotes} onChange={(e) => setQueueNotes(e.target.value)} placeholder="Additional details for the queuing team..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQueueOpen(false)}>Cancel</Button>
            <Button onClick={submitQueue} className="bg-amber-600 hover:bg-amber-700"><Layers className="h-4 w-4 mr-1.5" /> Send to Queue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ label, children }) => (<div><div className="text-xs text-slate-500">{label}</div><div className="font-medium text-sm">{children}</div></div>);

export default Inspections;
